import { useEffect, useState } from 'react'
import { Bell, ChevronDown, ChevronRight, Check, Send, MessageSquare, Mail } from 'lucide-react'

interface Settings {
  discordEnabled: boolean
  discordWebhookUrl: string | null
  emailEnabled: boolean
  emailTo: string | null
  smtpHost: string | null
  smtpPort: number | null
  smtpUser: string | null
  smtpFrom: string | null
  smtpSecure: boolean
  /** The server never sends the password itself, only whether one is stored. */
  smtpPassSet: boolean
  smtpSource: 'settings' | 'event' | 'env' | 'none'
}

const inputCls =
  'w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary'

const labelCls = 'block text-xs font-semibold text-muted-foreground mb-1'

/**
 * Report notification settings: Discord webhook and/or email, per admin.
 *
 * Collapsed by default, this is set-once configuration sitting above a queue
 * an admin checks regularly.
 */
export default function NotificationSettings() {
  const [open, setOpen] = useState(false)
  const [settings, setSettings] = useState<Settings | null>(null)
  const [smtpPass, setSmtpPass] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [testing, setTesting] = useState<'discord' | 'email' | null>(null)
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null)

  useEffect(() => {
    fetch('/api/notifications/settings')
      .then((r) => r.json())
      .then(setSettings)
      .catch(console.error)
  }, [])

  const update = <K extends keyof Settings>(key: K, value: Settings[K]) => {
    setSettings((prev) => (prev ? { ...prev, [key]: value } : prev))
    setSaved(false)
    setResult(null)
  }

  const save = async () => {
    if (!settings) return
    setSaving(true)
    setResult(null)
    try {
      const res = await fetch('/api/notifications/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        // Blank password means "leave the stored one alone".
        body: JSON.stringify({ ...settings, smtpPass }),
      })
      const body = await res.json()
      if (!res.ok) {
        setResult({ ok: false, message: body?.error || 'Could not save settings' })
        return
      }
      setSettings(body)
      setSmtpPass('')
      setSaved(true)
    } catch {
      setResult({ ok: false, message: 'Could not save settings' })
    } finally {
      setSaving(false)
    }
  }

  const sendTest = async (channel: 'discord' | 'email') => {
    setTesting(channel)
    setResult(null)
    try {
      const res = await fetch('/api/notifications/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Send what's on screen so a freshly pasted webhook can be tested
        // before saving. Blank fields fall back to the stored values server-side.
        body: JSON.stringify({ channel, settings: { ...settings, smtpPass } }),
      })
      const body = await res.json()
      setResult(
        res.ok
          ? { ok: true, message: `Test ${channel} notification sent.` }
          : { ok: false, message: body?.error || 'Test failed' }
      )
    } catch {
      setResult({ ok: false, message: 'Test failed' })
    } finally {
      setTesting(null)
    }
  }

  const active = settings
    ? [settings.discordEnabled && 'Discord', settings.emailEnabled && 'Email'].filter(Boolean).join(' + ')
    : ''

  return (
    <div className="rounded-xl border border-border bg-card/50">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
        <Bell className="h-4 w-4 text-primary" />
        <span className="text-sm font-semibold">Notifications</span>
        <span className="ml-auto text-xs text-muted-foreground">
          {active || 'Off'}
        </span>
      </button>

      {open && settings && (
        <div className="space-y-5 border-t border-border p-4">
          <p className="text-xs text-muted-foreground">
            Get alerted when a guest reports a photo at one of your events.
          </p>

          {/* Discord */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={settings.discordEnabled}
                onChange={(e) => update('discordEnabled', e.target.checked)}
                className="h-4 w-4 accent-primary"
              />
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
              Discord webhook
            </label>
            <input
              type="url"
              value={settings.discordWebhookUrl || ''}
              onChange={(e) => update('discordWebhookUrl', e.target.value)}
              placeholder="https://discord.com/api/webhooks/…"
              className={inputCls}
            />
            <p className="text-xs text-muted-foreground">
              Discord → Server Settings → Integrations → Webhooks → New Webhook → Copy URL.
              The report details are posted; the photo itself never is.
            </p>
            <button
              onClick={() => sendTest('discord')}
              disabled={testing !== null || !settings.discordWebhookUrl}
              className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50 transition-colors"
            >
              <Send className="h-3.5 w-3.5" />
              {testing === 'discord' ? 'Sending…' : 'Send test'}
            </button>
          </div>

          {/* Email */}
          <div className="space-y-2 border-t border-border pt-4">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={settings.emailEnabled}
                onChange={(e) => update('emailEnabled', e.target.checked)}
                className="h-4 w-4 accent-primary"
              />
              <Mail className="h-4 w-4 text-muted-foreground" />
              Email
            </label>

            <div>
              <label className={labelCls}>Send reports to</label>
              <input
                type="email"
                value={settings.emailTo || ''}
                onChange={(e) => update('emailTo', e.target.value)}
                placeholder="you@example.com"
                className={inputCls}
              />
            </div>

            <p className="pt-2 text-xs text-muted-foreground">
              SMTP server. Leave blank to reuse the credentials already configured
              {settings.smtpSource === 'env' ? ' on the server' : " in the event's sharing settings"}.
            </p>

            <div className="grid gap-2 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className={labelCls}>Host</label>
                <input
                  type="text"
                  value={settings.smtpHost || ''}
                  onChange={(e) => update('smtpHost', e.target.value)}
                  placeholder="smtp.example.com"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Port</label>
                <input
                  type="number"
                  value={settings.smtpPort ?? ''}
                  onChange={(e) => update('smtpPort', e.target.value === '' ? null : Number(e.target.value))}
                  placeholder="587"
                  className={inputCls}
                />
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={settings.smtpSecure}
                    onChange={(e) => update('smtpSecure', e.target.checked)}
                    className="h-4 w-4 accent-primary"
                  />
                  TLS (port 465)
                </label>
              </div>
              <div>
                <label className={labelCls}>Username</label>
                <input
                  type="text"
                  value={settings.smtpUser || ''}
                  onChange={(e) => update('smtpUser', e.target.value)}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>
                  Password {settings.smtpPassSet && !smtpPass && <span className="text-emerald-400">(saved)</span>}
                </label>
                <input
                  type="password"
                  value={smtpPass}
                  onChange={(e) => {
                    setSmtpPass(e.target.value)
                    setSaved(false)
                  }}
                  placeholder={settings.smtpPassSet ? 'Unchanged' : ''}
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>From address</label>
                <input
                  type="text"
                  value={settings.smtpFrom || ''}
                  onChange={(e) => update('smtpFrom', e.target.value)}
                  placeholder="PoseBox <noreply@example.com>"
                  className={inputCls}
                />
              </div>
            </div>

            <button
              onClick={() => sendTest('email')}
              disabled={testing !== null || !settings.emailTo}
              className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50 transition-colors"
            >
              <Send className="h-3.5 w-3.5" />
              {testing === 'email' ? 'Sending…' : 'Send test'}
            </button>
          </div>

          <div className="flex items-center gap-3 border-t border-border pt-4">
            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              {saved ? <Check className="h-4 w-4" /> : null}
              {saving ? 'Saving…' : saved ? 'Saved' : 'Save notifications'}
            </button>
            {/* Tests run against what's in the form, so the only thing Save is
                needed for is making the settings fire on real reports. */}
            <span className="text-xs text-muted-foreground">
              You can test before saving, but save to make it active.
            </span>
          </div>

          {result && (
            <p className={`text-sm ${result.ok ? 'text-emerald-400' : 'text-red-400'}`}>
              {result.message}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
